import React, {useState, useEffect} from "react";
import {LiaEyeSolid, LiaEyeSlashSolid} from "react-icons/lia";
import {CgProfile} from "react-icons/cg";
import {MdLockOutline} from "react-icons/md";
import {Moon} from "lucide-react";
import {useNavigate} from "react-router-dom";
import DarkModeToggle from "./../../components/Common/DarkModeToggle";
import useAuthStore from "./../../stores/authStore";
import secureStorage from "../../utils/secureStorage";

/**
 * Maps each backoffice_permissions key to its app route.
 * Order matters — the first key with permissions becomes the landing page.
 */
const PERMISSION_ROUTE_MAP = [
    {key: "dashboard", route: "/activite"},
    {key: "sales", route: "/ventes"},
    {key: "clients", route: "/clients"},
    {key: "catalogue", route: "/catalogue"},
    {key: "stock", route: "/stock"},
    {key: "waiting_list", route: "/liste-attente"},
    {key: "loyalty", route: "/fidelite"},
    {key: "returns", route: "/retours"},
    {key: "discounts", route: "/remises"},
    {key: "payments", route: "/paiements"},
    {key: "recipes", route: "/recettes"},
    {key: "purchase_orders", route: "/commandes"},
    {key: "providers", route: "/fournisseurs"},
    {key: "ecommerce", route: "/ecommerce"},
    {key: "calendar", route: "/calendrier"},
    {key: "planning", route: "/planning"},
    {key: "attendance", route: "/pointage"},
    {key: "employees", route: "/employes"},
    {key: "commissions", route: "/commissions"},
    {key: "accounting", route: "/comptabilite"},
    {key: "reports", route: "/rapports"},
    {key: "audit", route: "/audit"},
    {key: "notifications", route: "/notifications"},
    {key: "settings", route: "/parametres"},
];

/**
 * Returns the first route the user has at least one permission for.
 * Falls back to "/activite" if nothing matches (e.g. admin with full access).
 */
export const getFirstPermittedRoute = (permissions = {}) => {
    for (const {key, route} of PERMISSION_ROUTE_MAP) {
        if (Array.isArray(permissions[key]) && permissions[key].length > 0) {
            return route;
        }
    }
    return "/activite"; // fallback
};

const Login = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const {login, loading, error, isAuthenticated, clearErrors} = useAuthStore();

    useEffect(() => {
        // Clear previous errors when component mounts
        clearErrors();

        const checkAuth = async () => {
            if (isAuthenticated) {
                try {
                    // Check if there's already a selected account
                    const selectedAccount = await secureStorage.getItem('selectedAccount');

                    if (selectedAccount) {
                        // Re-use stored permissions to redirect correctly on page refresh
                        const user = await secureStorage.getItem('user');
                        const permissions = user?.backoffice_permissions ?? {};
                        navigate(getFirstPermittedRoute(permissions));
                    } else {
                        // Check if there are accounts to select from
                        const accounts = await secureStorage.getItem('accounts');
                        if (accounts && accounts.length > 0) {
                            navigate("/select-account");
                        }
                    }
                } catch (err) {
                    console.error("Error retrieving secure data:", err);
                }
            }
        };

        checkAuth();
    }, [isAuthenticated, navigate, clearErrors]);

    const togglePasswordVisibility = () => {
        setShowPassword((prevState) => !prevState);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const result = await login(email, password);

        if (result && result.success) {
            if (result.redirectTo === '/select-account') {
                navigate('/select-account');
            } else {
                // Determine the first screen the user has access to
                const permissions = result.user?.backoffice_permissions ?? {};
                navigate(getFirstPermittedRoute(permissions));
            }
        }
    };

    return (
        <div
            className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-white
                     to-[#E0F2F1] dark:from-gray-900 dark:to-gray-800 py-8">
            {/* Dark Mode Toggle */}
            <DarkModeToggle/>

            {/* Logo */}
            <div className="relative inline-block mb-16 text-center">
                <img src="/quickcom-logo-text.png" alt="QuickCom"
                     className="h-12 w-auto dark:filter dark:brightness-90"/>
                <span
                    title="Le mode sombre est disponible"
                    className="absolute -top-3 -right-2 sm:-right-4 flex items-center gap-1 whitespace-nowrap
                             bg-[#4DB6AC] dark:bg-primary-dark text-white text-[10px] font-medium
                             px-2 py-0.5 rounded-full shadow-sm">
                    <Moon className="w-2.5 h-2.5 flex-shrink-0"/>
                    Nouveau
                </span>
            </div>

            {/* Store Illustration */}
            <div className="mb-6 w-72 sm:w-96">
                <img
                    src="/login.png"
                    alt="QuickCom"
                    className="w-full h-auto max-h-90 dark:filter dark:brightness-90"
                />
            </div>

            {/* Description */}
            <p className="text-center text-base font-normal text-gray-700 dark:text-gray-300 mb-8">
                Saisissez votre mot de passe afin de vous<br/>
                connecter à votre compte QuickCom
            </p>

            {/* Login Form */}
            <form
                onSubmit={handleSubmit}
                className="w-full max-w-md px-6 sm:px-0"
            >
                {/* Email Input */}
                <div className="mb-4">
                    <div className="relative">
                        <div
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500">
                            <CgProfile className="w-5 h-5"/>
                        </div>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Adresse email"
                            required
                            className="w-full py-3.5 pl-12 pr-4 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-[#4DB6AC] dark:focus:border-primary-light"
                        />
                    </div>
                </div>

                {/* Password Input */}
                <div className="mb-6">
                    <div className="relative">
                        <div
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500">
                            <MdLockOutline className="w-5 h-5"/>
                        </div>
                        <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Mot de passe"
                            required
                            className={`w-full py-3.5 pl-12 pr-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none ${
                                error ? "border-red-500" : "focus:border-[#4DB6AC] dark:focus:border-primary-light"
                            }`}
                        />
                        <button
                            type="button"
                            onClick={togglePasswordVisibility}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-[#4DB6AC] dark:hover:text-primary-light"
                            aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                        >
                            {showPassword ? (
                                <LiaEyeSlashSolid className="w-5 h-5"/>
                            ) : (
                                <LiaEyeSolid className="w-5 h-5"/>
                            )}
                        </button>
                    </div>
                    {error && (
                        <p className="text-sm text-red-500 dark:text-red-400 mt-1 text-center">
                            {error}
                        </p>
                    )}
                </div>

                {/* Login Button */}
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-[#4DB6AC] dark:bg-primary-dark text-white font-medium rounded-md hover:bg-[#00897B] dark:hover:bg-primary-darker transition-colors duration-200 disabled:opacity-70"
                >
                    {loading ? "Connexion en cours..." : "Connexion"}
                </button>

                {/* Forgot Password */}
                <div className="mt-2 text-center">
                    <button
                        type="button"
                        className="text-sm px-6 py-2 rounded-full bg-[#E0F2F1] dark:bg-gray-700 text-[#00897B] dark:text-primary-light"
                        onClick={() => navigate("/forgoutPassword")}
                    >
                        J'ai oublié mon mot de passe
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Login;
